import { Directive, OnDestroy, input, output } from '@angular/core';
import { Subscription, fromEvent } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Directive({
  // eslint-disable-next-line @angular-eslint/directive-selector
  selector: '[long-press]',
  host: {
    '(mousedown)': 'onMouseDown($event)',
    '[class.press]': 'pressing',
    '[class.longpress]': 'isLongPressing'
  }
})
export class LongPressDirective implements OnDestroy {
  readonly pressEnabled = input<boolean>(true);
  readonly pressModel = input<unknown>(undefined);
  readonly duration = input<number>(500);

  readonly longPressStart = output<{ event: MouseEvent; model: unknown }>();
  readonly longPressing = output<{ event: MouseEvent; model: unknown }>();
  readonly longPressEnd = output<{ event?: MouseEvent; model: unknown }>();

  pressing: boolean;
  isLongPressing: boolean;
  timeout: ReturnType<typeof setTimeout>;
  mouseX: number = 0;
  mouseY: number = 0;

  subscription: Subscription;

  onMouseDown(event: MouseEvent): void {
    // don't do right/middle clicks
    if (event.which !== 1 || !this.pressEnabled()) {return;}

    // don't start drag if its on resize handle
    const target = event.target as HTMLElement;
    if (target.classList.contains('resize-handle')) {return;}

    this.mouseX = event.clientX;
    this.mouseY = event.clientY;

    this.pressing = true;
    this.isLongPressing = false;

    const mouseup = fromEvent(document, 'mouseup');
    this.subscription = mouseup.subscribe(() => this.onMouseup());

    this.timeout = setTimeout(() => {
      this.isLongPressing = true;
      this.longPressStart.emit({
        event,
        model: this.pressModel()
      });

      this.subscription.add(
        fromEvent(document, 'mousemove')
          .pipe(takeUntil(mouseup))
          .subscribe((mouseEvent: Event) => this.onMouseMove(mouseEvent as MouseEvent))
      );

      this.loop(event);
    }, this.duration());

    this.loop(event);
  }

  onMouseMove(event: MouseEvent): void {
    if (this.pressing && !this.isLongPressing) {
      const xThres = Math.abs(event.clientX - this.mouseX) > 10;
      const yThres = Math.abs(event.clientY - this.mouseY) > 10;

      if (xThres || yThres) {
        this.endPress();
      }
    }
  }

  loop(event: MouseEvent): void {
    if (this.isLongPressing) {
      this.timeout = setTimeout(() => {
        this.longPressing.emit({
          event,
          model: this.pressModel()
        });
        this.loop(event);
      }, 50);
    }
  }

  endPress(): void {
    clearTimeout(this.timeout);
    this.isLongPressing = false;
    this.pressing = false;
    this._destroySubscription();

    this.longPressEnd.emit({
      model: this.pressModel()
    });
  }

  onMouseup(): void {
    this.endPress();
  }

  ngOnDestroy(): void {
    this._destroySubscription();
  }

  private _destroySubscription(): void {
    if (this.subscription) {
      this.subscription.unsubscribe();
      this.subscription = undefined;
    }
  }
}
